import { AuthorSignature } from '@/components/AuthorSignature';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Play, Square, Plus, Minus, Hand, Share2, Volume2, VolumeX, Trash2, Power, PowerOff, Copy, Crosshair } from 'lucide-react';
import { Slider } from '../components/ui/slider';
import { Input } from '../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Toaster, toast } from '../components/ui/sonner';

import { getEngine } from '../lib/engines';
import { SOUND_TYPES, KIT_PRESETS } from '../lib/options';
import useWakeLock from '../lib/useWakeLock';
import { buildShareUrl } from '../lib/shareLink';
import TopNav from '../components/TopNav';
import { useMidiTransport } from '../hooks/useMidi';
import QRShareModal from '../components/QRShareModal';
import { useBarGestures } from '../components/sequencer/useBarGestures';
import { DeleteBarDialog } from '../components/sequencer/DeleteBarDialog';

const STORAGE_KEY_SEQ = 'metronomo:sequencer:v1';

const MIN_BPM = 30;
const MAX_BPM = 300;
const MAX_BARS = 100;
const MAX_NUMERATOR = 32;
const MIN_NUMERATOR = 1;
const DENOMINATOR_OPTIONS = [4, 8, 16, 32];

const clampBpm = (n) => Math.max(MIN_BPM, Math.min(MAX_BPM, Math.round(n) || MIN_BPM));
const clampNumerator = (n) => Math.max(MIN_NUMERATOR, Math.min(MAX_NUMERATOR, Math.round(n) || MIN_NUMERATOR));

// Each bar describes a time signature (numerator/denominator) and a per-cell pattern.
// Number of cells in the bar === numerator. Each cell lasts (4/denominator) quarter-note.
function makeBar(numerator = 4, denominator = 4) {
  const n = clampNumerator(numerator);
  // First beat = high accent (sky), other beats = green accent
  const pattern = Array.from({ length: n }, (_, i) => (i === 0 ? 3 : 2));
  return { numerator: n, denominator, pattern, muted: false };
}

function loadSeq() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_SEQ);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { return null; }
}

// Backward-compat: previous shape used { beatsPerBar, subdivision, pattern }.
// Migrate to { numerator, denominator } where numerator = beatsPerBar * subdivision and
// denominator = 4 * subdivision (so that musical duration is preserved).
function migrateBar(b, fallbackBeats = 4) {
  if (b && typeof b.numerator === 'number' && typeof b.denominator === 'number') {
    return {
      numerator: clampNumerator(b.numerator),
      denominator: DENOMINATOR_OPTIONS.includes(b.denominator) ? b.denominator : 4,
      pattern: Array.isArray(b.pattern) ? b.pattern : Array.from({ length: clampNumerator(b.numerator) }, (_, i) => (i === 0 ? 3 : 2)),
      muted: !!b.muted,
    };
  }
  const beats = b?.beatsPerBar ?? fallbackBeats;
  const sub = b?.subdivision ?? 1;
  const numerator = clampNumerator(beats * sub);
  const denominator = sub === 1 ? 4 : sub === 2 ? 8 : sub === 4 ? 16 : 4;
  const pattern = Array.isArray(b?.pattern) && b.pattern.length === numerator
    ? b.pattern
    : Array.from({ length: numerator }, (_, i) => (i === 0 ? 3 : 2));
  return { numerator, denominator, pattern, muted: !!b?.muted };
}

const STATE_BTN = (s) =>
  s === 0 ? 'bg-[#0e0e10] border-white/10 hover:border-white/30 text-white/25'
    : s === 1 ? 'bg-pink-500/12 border-pink-500/55 text-pink-300 shadow-[0_0_10px_rgba(236,72,153,0.22)]'
      : s === 2 ? 'bg-emerald-500/15 border-emerald-400/70 text-emerald-300 shadow-[0_0_14px_rgba(16,185,129,0.35)]'
        : 'bg-sky-500/15 border-sky-400/70 text-sky-200 shadow-[0_0_14px_rgba(56,189,248,0.4)]';

// Single cell button (3-state cycle off → click → accent)
const Cell = React.memo(function Cell({ idx, state, isCurrent, isBeatStart, disabled, onCycle }) {
  return (
    <button
      type="button"
      data-testid={`seq-cell-${idx}`}
      aria-label={`Cell ${idx + 1} state ${state}`}
      disabled={disabled}
      onClick={() => onCycle(idx)}
      className={`relative h-10 sm:h-11 md:h-12 rounded-md border transition-all flex items-center justify-center cursor-pointer select-none ${
        disabled ? 'opacity-40 cursor-not-allowed' : ''
      } ${STATE_BTN(state)} ${
        isCurrent ? 'ring-2 ring-white scale-[1.08] brightness-150 z-10' : ''
      } ${isBeatStart ? 'border-l-2 border-l-white/30' : ''}`}
    >
      <span className="text-[10px] font-mono opacity-70">{state === 3 ? '◆' : state === 2 ? '●' : state === 1 ? '∙' : ''}</span>
    </button>
  );
});

function BarRow({ idx, bar, isCurrent, currentCell, isPlaying, onCycleCell, onSetNumerator, onSetDenominator, onToggleMute, onCopyBar, onResetBar, onRequestDelete, onDuplicateBar }) {
  const cells = bar.pattern.length;
  const numerator = bar.numerator || 4;
  const denominator = bar.denominator || 4;
  const { offset, handlers } = useBarGestures({
    onSwipeLeft: () => onRequestDelete(idx),
    onLongPress: () => onToggleMute(idx),
    onDoubleTap: () => onDuplicateBar(idx),
  });

  return (
    <div className="relative rounded-xl overflow-hidden">
      <div
        aria-hidden
        className="absolute inset-0 flex items-center justify-end pr-6 bg-rose-500/20 text-rose-300 text-[10px] uppercase tracking-[0.3em] gap-2 transition-opacity"
        style={{ opacity: offset < 0 ? Math.min(1, -offset / 90) : 0 }}
      >
        <Trash2 className="w-4 h-4" /> Elimina
      </div>
    <div
      data-testid={`seq-bar-${idx}`}
      {...handlers}
      style={{ transform: offset ? `translateX(${offset}px)` : undefined, touchAction: 'pan-y', transition: offset ? 'none' : 'transform 200ms ease-out' }}
      className={`relative select-none rounded-xl p-2.5 sm:p-4 border transition-[background-color,border-color,box-shadow] ${
        isCurrent && isPlaying
          ? 'bg-[#120c10] border-pink-400/30 shadow-[0_0_20px_rgba(236,72,153,0.1)]'
          : 'bg-[#0f0f10] border-white/5 hover:border-white/15'
      }`}
    >
      {/* Header row 1 — bar number, time signature, action buttons */}
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <span className="font-bebas text-3xl sm:text-3xl text-white/70 tabular-nums w-9 text-center shrink-0">{idx + 1}</span>

          {/* Time signature stepper */}
          <div className="flex items-center gap-1 bg-[#0a0a0a] rounded-md border border-white/10 px-1 py-0.5 shrink-0" title="Tempo / time signature">
            <button
              data-testid={`seq-bar-${idx}-num-down`}
              onClick={() => onSetNumerator(idx, numerator - 1)}
              disabled={numerator <= MIN_NUMERATOR}
              className="h-9 w-9 rounded hover:bg-white/5 disabled:opacity-30 flex items-center justify-center text-white/60"
              aria-label="Diminuisci numeratore"
            >
              <Minus className="w-4 h-4" />
            </button>
            <div className="flex flex-col items-center justify-center leading-none px-1 min-w-[2.2rem]">
              <span data-testid={`seq-bar-${idx}-num`} className="font-bebas text-lg sm:text-xl tabular-nums text-pink-300">{numerator}</span>
              <div className="w-full h-px bg-white/30 my-0.5" />
              <span data-testid={`seq-bar-${idx}-den`} className="font-bebas text-lg sm:text-xl tabular-nums text-white/80">{denominator}</span>
            </div>
            <button
              data-testid={`seq-bar-${idx}-num-up`}
              onClick={() => onSetNumerator(idx, numerator + 1)}
              disabled={numerator >= MAX_NUMERATOR}
              className="h-9 w-9 rounded hover:bg-white/5 disabled:opacity-30 flex items-center justify-center text-white/60"
              aria-label="Aumenta numeratore"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="flex items-center gap-1 sm:gap-2 shrink-0">
          <button
            data-testid={`seq-bar-mute-${idx}`}
            onClick={() => onToggleMute(idx)}
            className={`h-10 w-10 rounded-md border flex items-center justify-center transition-all ${
              bar.muted
                ? 'border-rose-400/50 bg-rose-500/10 text-rose-300'
                : 'border-pink-400/50 bg-pink-500/10 text-pink-300 shadow-[0_0_10px_rgba(236,72,153,0.3)]'
            }`}
            aria-label={bar.muted ? 'Unmute bar' : 'Mute bar'}
            title={bar.muted ? 'Riattiva' : 'Mute'}
          >
            {bar.muted ? <PowerOff className="w-4 h-4" /> : <Power className="w-4 h-4" />}
          </button>
          <button
            data-testid={`seq-bar-copy-${idx}`}
            onClick={() => onCopyBar(idx)}
            className="h-10 w-10 rounded-md border border-white/10 hover:border-white/30 hover:bg-white/5 flex items-center justify-center transition-all text-white/60"
            aria-label="Copia bar"
            title="Copia bar successiva"
          >
            <Copy className="w-4 h-4" />
          </button>
          <button
            data-testid={`seq-bar-reset-${idx}`}
            onClick={() => onResetBar(idx)}
            className="h-10 w-10 rounded-md border border-white/10 hover:border-white/30 hover:bg-white/5 flex items-center justify-center transition-all text-white/60"
            aria-label="Reset bar"
            title="Reset"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Header row 2 — denominator pills (horizontal) */}
      <div className="flex items-center gap-1 mb-3 overflow-x-auto no-scrollbar">
        <span className="text-[9px] uppercase tracking-widest text-white/30 mr-1 shrink-0">Nota</span>
        {DENOMINATOR_OPTIONS.map((d) => (
          <button
            key={d}
            data-testid={`seq-bar-${idx}-den-${d}`}
            onClick={() => onSetDenominator(idx, d)}
            className={`shrink-0 px-3 h-8 rounded-md text-xs font-mono tracking-wider transition-all border ${
              denominator === d
                ? 'bg-pink-400 text-black border-pink-400'
                : 'text-white/55 border-white/10 hover:border-white/30 hover:text-white'
            }`}
            aria-label={`Denominatore ${d}`}
          >
            1/{d}
          </button>
        ))}
      </div>

      <div className="overflow-x-auto -mx-1 px-1" data-no-gesture>
        <div
          className="grid gap-1 sm:gap-1.5 min-w-fit"
          style={{ gridTemplateColumns: `repeat(${cells}, minmax(22px, 1fr))` }}
        >
          {bar.pattern.map((state, i) => (
            <Cell
              key={i}
              idx={i}
              state={state}
              isCurrent={isPlaying && isCurrent && currentCell === i}
              isBeatStart={i === 0}
              disabled={bar.muted}
              onCycle={(c) => onCycleCell(idx, c)}
            />
          ))}
        </div>
      </div>
    </div>
    </div>
  );
}

export default function SequencerPage() {
  const stored = useMemo(() => loadSeq(), []);

  const [bpm, setBpm] = useState(stored?.bpm ?? 100);
  const [bpmInput, setBpmInput] = useState(String(stored?.bpm ?? 100));
  const [bars, setBars] = useState(() => {
    if (stored?.bars && Array.isArray(stored.bars)) {
      const fallbackBeats = stored.beatsPerBar ?? 4;
      return stored.bars.map((b) => migrateBar(b, fallbackBeats));
    }
    return Array.from({ length: 4 }, () => makeBar(4, 4));
  });
  const [soundType, setSoundType] = useState(stored?.soundType ?? 'square');
  const [kitMode, setKitMode] = useState(stored?.kitMode ?? false);
  const [soundClick, setSoundClick] = useState(stored?.soundClick ?? 'hihat');
  const [soundBeat, setSoundBeat] = useState(stored?.soundBeat ?? 'snare');
  const [soundHigh, setSoundHigh] = useState(stored?.soundHigh ?? 'kick');
  const [volume, setVolume] = useState(stored?.volume ?? 0.7);
  const [muted, setMuted] = useState(false);

  const [isPlaying, setIsPlaying] = useState(() => getEngine('sequencer').isRunning());
  const [currentBar, setCurrentBar] = useState(-1);
  const [currentCell, setCurrentCell] = useState(-1);

  const [shareModal, setShareModal] = useState(null);

  const engineRef = useRef(null);
  const tapTimesRef = useRef([]);

  if (!engineRef.current) engineRef.current = getEngine('sequencer');

  useWakeLock(isPlaying);

  // ===== Engine wiring =====
  useEffect(() => { engineRef.current.setBpm(bpm); }, [bpm]);
  useEffect(() => { engineRef.current.setSoundType(soundType); }, [soundType]);
  useEffect(() => {
    const e = engineRef.current;
    if (kitMode) {
      e.setSoundTypeForState(1, soundClick);
      e.setSoundTypeForState(2, soundBeat);
      e.setSoundTypeForState(3, soundHigh);
    } else {
      e.setSoundTypeForState(1, null);
      e.setSoundTypeForState(2, null);
      e.setSoundTypeForState(3, null);
    }
  }, [kitMode, soundClick, soundBeat, soundHigh]);
  useEffect(() => { engineRef.current.setVolume(muted ? 0 : volume); }, [volume, muted]);
  useEffect(() => { engineRef.current.setMode('sequencer'); }, []);
  useEffect(() => {
    // Engine still expects { beatsPerBar, pattern, muted } — translate from time-signature.
    // For per-cell duration: cellDuration = (60/bpm) × (4/denominator).
    // To keep the shared engine API, we pass beatsPerBar = numerator × (4/denominator)
    // so that engine's barDuration = (60/bpm) × beatsPerBar matches musical duration.
    const enginBars = bars.map((b) => ({
      beatsPerBar: (b.numerator || 4) * (4 / (b.denominator || 4)),
      pattern: b.pattern,
      muted: !!b.muted,
    }));
    engineRef.current.setSequencerBars(enginBars);
  }, [bars]);

  useEffect(() => {
    const e = engineRef.current;
    e.onBeat = (info) => {
      setCurrentBar(info.bar);
      setCurrentCell(info.cell);
    };
    return () => { e.onBeat = null; };
  }, []);

  // Persist
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY_SEQ, JSON.stringify({
        bpm, bars, soundType, volume, kitMode, soundClick, soundBeat, soundHigh,
      }));
    } catch { /* ignore */ }
  }, [bpm, bars, soundType, volume, kitMode, soundClick, soundBeat, soundHigh]);

  // Keeps playing after leaving the page; resync UI when stopped from elsewhere.
  useEffect(() => engineRef.current.onRunning((running) => {
    setIsPlaying(running);
    if (!running) { setCurrentBar(-1); setCurrentCell(-1); }
  }), []);

  const togglePlay = useCallback(() => {
    const e = engineRef.current;
    if (e.isRunning()) {
      e.stop();
      setIsPlaying(false);
      setCurrentBar(-1);
      setCurrentCell(-1);
    } else {
      e.start();
      setIsPlaying(true);
    }
  }, []);

  // (Auto-scroll to current bar disabled — user can scroll freely during playback)

  // BPM
  const applyBpm = (v) => {
    const c = clampBpm(v);
    setBpm(c);
    setBpmInput(String(c));
  };
  useMidiTransport({ isPlaying, togglePlay, setBpm: applyBpm, getBpm: () => engineRef.current.effectiveBpm() });
  const handleTap = () => {
    const now = performance.now();
    const arr = tapTimesRef.current.filter((t) => now - t < 2000);
    arr.push(now);
    tapTimesRef.current = arr.slice(-5);
    if (arr.length >= 2) {
      const taps = tapTimesRef.current;
      const intervals = [];
      for (let i = 1; i < taps.length; i++) intervals.push(taps[i] - taps[i - 1]);
      const avg = intervals.reduce((a, b) => a + b, 0) / intervals.length;
      applyBpm(60000 / avg);
    }
  };

  const [barsCountInput, setBarsCountInput] = useState('');
  useEffect(() => { setBarsCountInput(String(bars.length)); }, [bars.length]);

  const handleSetBarsCount = (n) => {
    const target = Math.max(1, Math.min(MAX_BARS, Math.round(n) || 1));
    setBars((prev) => {
      if (target === prev.length) return prev;
      if (target < prev.length) return prev.slice(0, target);
      const last = prev[prev.length - 1];
      const additions = Array.from({ length: target - prev.length }, () =>
        makeBar(last?.numerator ?? 4, last?.denominator ?? 4));
      return [...prev, ...additions];
    });
  };

  const handleAddBar = () => handleSetBarsCount(bars.length + 1);
  const handleRemoveBar = () => handleSetBarsCount(bars.length - 1);
  const handleAddManyBars = (n) => handleSetBarsCount(bars.length + n);

  const handleCycleCell = useCallback((barIdx, cellIdx) => {
    setBars((prev) => {
      const next = prev.slice();
      const bar = next[barIdx];
      if (!bar) return prev;
      const newPattern = bar.pattern.slice();
      newPattern[cellIdx] = (newPattern[cellIdx] + 1) % 4;
      next[barIdx] = { ...bar, pattern: newPattern };
      return next;
    });
  }, []);

  const handleSetNumerator = useCallback((barIdx, n) => {
    const target = clampNumerator(n);
    setBars((prev) => {
      const next = prev.slice();
      const bar = next[barIdx];
      if (!bar) return prev;
      let pattern = bar.pattern.slice(0, target);
      while (pattern.length < target) pattern.push(2);
      if (target > 0 && (pattern[0] === 0 || pattern[0] === 1)) pattern[0] = 3;
      next[barIdx] = { ...bar, numerator: target, pattern };
      return next;
    });
  }, []);

  const handleSetDenominator = useCallback((barIdx, denominator) => {
    if (!DENOMINATOR_OPTIONS.includes(denominator)) return;
    setBars((prev) => {
      const next = prev.slice();
      const bar = next[barIdx];
      if (!bar) return prev;
      // Changing denominator only affects per-cell duration; pattern is preserved.
      next[barIdx] = { ...bar, denominator };
      return next;
    });
  }, []);

  const handleToggleMute = useCallback((barIdx) => {
    setBars((prev) => {
      const next = prev.slice();
      next[barIdx] = { ...next[barIdx], muted: !next[barIdx].muted };
      return next;
    });
  }, []);

  const handleCopyBar = useCallback((barIdx) => {
    setBars((prev) => {
      if (barIdx >= prev.length - 1) return prev;
      const next = prev.slice();
      next[barIdx + 1] = {
        ...prev[barIdx],
        pattern: prev[barIdx].pattern.slice(),
      };
      return next;
    });
    toast.success('Bar copiata su quella successiva');
  }, []);

  const handleDuplicateBar = useCallback((barIdx) => {
    if (bars.length >= MAX_BARS) {
      toast.error(`Massimo ${MAX_BARS} bar`);
      return;
    }
    setBars((prev) => {
      const next = prev.slice();
      next.splice(barIdx + 1, 0, { ...prev[barIdx], pattern: prev[barIdx].pattern.slice() });
      return next;
    });
    toast.success(`Bar ${barIdx + 1} duplicata`);
  }, [bars.length]);

  const [pendingDelete, setPendingDelete] = useState(null);
  const handleRequestDelete = useCallback((barIdx) => {
    if (bars.length <= 1) {
      toast.error('Serve almeno una bar');
      return;
    }
    setPendingDelete(barIdx);
  }, [bars.length]);
  const handleConfirmDelete = () => {
    const idx = pendingDelete;
    const removed = bars[idx];
    if (bars.length <= 1 || !removed) return;
    setBars((prev) => prev.filter((_, i) => i !== idx));
    setPendingDelete(null);
    toast(`Bar ${idx + 1} eliminata`, {
      duration: 6000,
      action: {
        label: 'Annulla',
        onClick: () => setBars((prev) => {
          if (prev.length >= MAX_BARS) return prev;
          const next = prev.slice();
          next.splice(Math.min(idx, next.length), 0, removed);
          return next;
        }),
      },
    });
  };

  const handleResetBar = useCallback((barIdx) => {
    setBars((prev) => {
      const next = prev.slice();
      const bar = next[barIdx];
      next[barIdx] = makeBar(bar?.numerator || 4, bar?.denominator || 4);
      return next;
    });
  }, []);

  const handleResetAll = () => {
    setBars((prev) => prev.map((b) => makeBar(b.numerator || 4, b.denominator || 4)));
    toast('Sequencer reset');
  };

  const handleShare = () => {
    const first = bars[0] || { numerator: 4, denominator: 4, pattern: [2, 1, 1, 1] };
    const data = {
      name: 'Sequencer pattern',
      bpm,
      signature: `${first.numerator}/${first.denominator}`,
      pattern: first.pattern,
      barsEnabled: bars.map((b) => !b.muted),
      subdivision: 1,
      soundType,
      volume,
      mode: 'single',
      sections: [],
      loopSong: true,
      countInBars: 0,
      rampEnabled: false,
      rampDelta: 2,
      rampEveryBars: 4,
      rampMaxBpm: 300,
    };
    const url = buildShareUrl(data);
    setShareModal({ url, title: 'Sequencer pattern', subtitle: `${bpm} bpm · ${bars.length} bar` });
  };

  return (
    <div className="relative min-h-screen text-white pb-8" data-testid="sequencer-root">
      <div className="bg-foam" />
      <div className="bg-grain" />

      <div className="relative z-10 max-w-6xl mx-auto px-3 sm:px-4 md:px-8 py-3 md:py-6 flex flex-col gap-3 sm:gap-5">
        <TopNav />

        {/* Top transport — Play + BPM + Tap (sticky on mobile so playback is always reachable) */}
        <section className="bg-[#111112] border border-white/5 rounded-2xl p-3 sm:p-6 shadow-xl sticky top-[52px] sm:static z-30" style={{ backdropFilter: 'blur(8px)' }}>
          <div className="flex items-center gap-3 sm:gap-5">
            {/* PLAY / STOP — primary CTA */}
            <button
              data-testid="seq-play-stop-btn"
              onClick={togglePlay}
              aria-label={isPlaying ? 'Stop' : 'Play'}
              className={`shrink-0 h-14 w-14 sm:h-20 sm:w-20 rounded-full flex flex-col items-center justify-center font-bebas text-[10px] sm:text-xs tracking-widest active:scale-95 transition-all ${
                isPlaying
                  ? 'bg-rose-500 text-white shadow-[0_0_24px_rgba(244,63,94,0.5)]'
                  : 'bg-pink-400 text-black shadow-[0_0_24px_rgba(236,72,153,0.5)]'
              }`}
            >
              {isPlaying ? <Square className="w-5 h-5 sm:w-7 sm:h-7" /> : <Play className="w-5 h-5 sm:w-7 sm:h-7 ml-0.5" />}
              <span className="mt-0.5">{isPlaying ? 'STOP' : 'PLAY'}</span>
            </button>

            {/* BPM */}
            <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
              <div className="flex items-baseline gap-1">
                <Input
                  data-testid="seq-bpm-input"
                  type="number"
                  min={MIN_BPM}
                  max={MAX_BPM}
                  value={bpmInput}
                  onChange={(e) => setBpmInput(e.target.value)}
                  onBlur={() => applyBpm(parseInt(bpmInput, 10))}
                  onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                  className="font-bebas text-4xl sm:text-7xl h-auto bg-transparent border-0 px-0 tracking-tight tabular-nums leading-none w-[4.6ch] sm:w-[5.5ch] focus-visible:ring-0 focus-visible:border-b-2 focus-visible:border-pink-400"
                />
                <span className="text-[10px] sm:text-xs tracking-[0.3em] uppercase text-white/50 mb-1 sm:mb-2">BPM</span>
              </div>
              <div className="flex items-center gap-1 sm:hidden">
                <button data-testid="seq-bpm-down" onClick={() => applyBpm(bpm - 1)} className="h-8 w-8 rounded border border-white/10 hover:border-white/30 flex items-center justify-center"><Minus className="w-3 h-3" /></button>
                <button data-testid="seq-bpm-up" onClick={() => applyBpm(bpm + 1)} className="h-8 w-8 rounded border border-white/10 hover:border-white/30 flex items-center justify-center"><Plus className="w-3 h-3" /></button>
                <span className="text-[9px] uppercase tracking-widest text-white/30 ml-1">{MIN_BPM}-{MAX_BPM}</span>
              </div>
              <div className="hidden sm:flex flex-col shrink-0">
                <div className="flex items-center gap-1">
                  <button onClick={() => applyBpm(bpm - 1)} className="h-7 w-7 rounded border border-white/10 hover:border-white/30 flex items-center justify-center"><Minus className="w-3 h-3" /></button>
                  <button onClick={() => applyBpm(bpm + 1)} className="h-7 w-7 rounded border border-white/10 hover:border-white/30 flex items-center justify-center"><Plus className="w-3 h-3" /></button>
                </div>
                <div className="text-[9px] uppercase tracking-widest text-white/30 mt-1">{MIN_BPM}-{MAX_BPM}</div>
              </div>
              <Slider
                data-testid="seq-bpm-slider"
                value={[bpm]}
                min={MIN_BPM}
                max={MAX_BPM}
                step={1}
                onValueChange={(v) => applyBpm(v[0])}
                className="hidden md:flex flex-1 min-w-[160px]"
              />
            </div>

            {/* TAP */}
            <button
              data-testid="seq-tap-btn"
              onClick={handleTap}
              className="shrink-0 h-14 w-14 sm:h-20 sm:w-20 rounded-full border border-white/15 hover:border-white/35 hover:bg-white/5 flex flex-col items-center justify-center gap-0.5 text-[10px] sm:text-xs uppercase tracking-widest font-bebas active:scale-95 transition-all"
              aria-label="Tap tempo"
            >
              <Hand className="w-4 h-4 sm:w-6 sm:h-6" />
              <span>Tap</span>
            </button>
          </div>

          {/* Playback indicator + jump-to-current-bar button */}
          {isPlaying && currentBar >= 0 && (
            <button
              type="button"
              data-testid="seq-now-playing"
              onClick={() => {
                const el = document.querySelector(`[data-testid="seq-bar-${currentBar}"]`);
                if (el?.scrollIntoView) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
              }}
              className="w-full mt-3 pt-3 border-t border-white/5 flex items-center justify-between gap-2 group cursor-pointer hover:bg-white/[0.02] -mx-1 px-1 rounded-md transition-colors"
              aria-label="Vai alla bar in riproduzione"
              title="Vai alla bar in riproduzione"
            >
              <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 group-hover:text-white/60 flex items-center gap-1.5">
                <Crosshair className="w-3 h-3" /> Vai alla bar
              </div>
              <div className="font-bebas text-base text-pink-300 tabular-nums">
                BAR {currentBar + 1} / {bars.length}
              </div>
            </button>
          )}
        </section>

        {/* Pattern config */}
        <section className="bg-[#111112] border border-white/5 rounded-2xl p-3 sm:p-5 shadow-xl space-y-3 sm:space-y-4">
          <div>
            <div className="flex items-center justify-between gap-2 mb-1.5">
              <label className="text-[10px] uppercase tracking-[0.2em] text-white/50">Numero bar · max {MAX_BARS}</label>
              <div className="flex items-center gap-1">
                <button
                  data-testid="seq-bars-add-5"
                  onClick={() => handleAddManyBars(5)}
                  disabled={bars.length >= MAX_BARS}
                  className="h-7 px-2 rounded-md border border-white/10 hover:border-pink-400/40 hover:bg-pink-500/5 disabled:opacity-30 text-[10px] font-mono uppercase tracking-widest text-white/70"
                >
                  +5
                </button>
                <button
                  data-testid="seq-bars-add-10"
                  onClick={() => handleAddManyBars(10)}
                  disabled={bars.length >= MAX_BARS}
                  className="h-7 px-2 rounded-md border border-white/10 hover:border-pink-400/40 hover:bg-pink-500/5 disabled:opacity-30 text-[10px] font-mono uppercase tracking-widest text-white/70"
                >
                  +10
                </button>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                data-testid="seq-bars-down"
                onClick={handleRemoveBar}
                disabled={bars.length <= 1}
                className="h-10 w-10 sm:h-12 sm:w-12 rounded-md border border-white/10 hover:border-white/30 hover:bg-white/5 flex items-center justify-center disabled:opacity-30 shrink-0"
              >
                <Minus className="w-4 h-4" />
              </button>
              <Input
                data-testid="seq-bars-count"
                type="number"
                min={1}
                max={MAX_BARS}
                value={barsCountInput}
                onChange={(e) => setBarsCountInput(e.target.value)}
                onBlur={() => handleSetBarsCount(parseInt(barsCountInput, 10))}
                onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                className="h-10 sm:h-12 flex-1 text-center font-bebas text-2xl sm:text-3xl bg-[#0A0A0A] border-white/10 tabular-nums focus-visible:ring-1 focus-visible:ring-pink-400"
              />
              <button
                data-testid="seq-bars-up"
                onClick={handleAddBar}
                disabled={bars.length >= MAX_BARS}
                className="h-10 w-10 sm:h-12 sm:w-12 rounded-md border border-white/10 hover:border-white/30 hover:bg-white/5 flex items-center justify-center disabled:opacity-30 shrink-0"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-[10px] uppercase tracking-[0.2em] text-white/50">{kitMode ? 'Kit Drum' : 'Suono'}</label>
                <button
                  data-testid="seq-kit-toggle"
                  type="button"
                  onClick={() => setKitMode((v) => !v)}
                  className={`text-[9px] uppercase tracking-widest h-6 px-2 rounded-full border transition-all ${
                    kitMode
                      ? 'bg-pink-500/15 border-pink-400/60 text-pink-300 shadow-[0_0_10px_rgba(236,72,153,0.3)]'
                      : 'border-white/15 text-white/45 hover:border-white/30 hover:text-white/70'
                  }`}
                >
                  Kit {kitMode ? 'ON' : 'OFF'}
                </button>
              </div>
              {!kitMode ? (
                <Select value={soundType} onValueChange={setSoundType}>
                  <SelectTrigger data-testid="seq-sound-trigger" className="bg-[#0A0A0A] border-white/10 text-white h-11 font-bebas text-lg tracking-wide">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#111112] border-white/10 text-white">
                    {SOUND_TYPES.map((s) => (
                      <SelectItem key={s.id} value={s.id} className="font-bebas focus:bg-pink-500/20 focus:text-pink-200">{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <div className="space-y-2">
                  {/* Kit presets — quick chips */}
                  <div className="flex items-center gap-1 overflow-x-auto no-scrollbar -mx-1 px-1 pb-1">
                    <span className="text-[9px] uppercase tracking-widest text-white/30 mr-1 shrink-0">Preset</span>
                    {KIT_PRESETS.map((p) => {
                      const active = soundClick === p.click && soundBeat === p.beat && soundHigh === p.high;
                      return (
                        <button
                          key={p.id}
                          type="button"
                          data-testid={`seq-kit-preset-${p.id}`}
                          onClick={() => { setSoundClick(p.click); setSoundBeat(p.beat); setSoundHigh(p.high); }}
                          className={`shrink-0 h-7 px-2.5 rounded-full text-[10px] font-mono uppercase tracking-widest transition-all border ${
                            active
                              ? 'bg-pink-400 text-black border-pink-400 shadow-[0_0_10px_rgba(236,72,153,0.4)]'
                              : 'border-white/15 text-white/55 hover:border-pink-400/50 hover:text-pink-200'
                          }`}
                        >
                          {p.label}
                        </button>
                      );
                    })}
                  </div>
                  {[
                    { state: 1, label: 'Click', dot: 'bg-pink-500', value: soundClick, set: setSoundClick, testid: 'seq-kit-click' },
                    { state: 2, label: 'Beat', dot: 'bg-emerald-500', value: soundBeat, set: setSoundBeat, testid: 'seq-kit-beat' },
                    { state: 3, label: 'Acuto', dot: 'bg-sky-500', value: soundHigh, set: setSoundHigh, testid: 'seq-kit-high' },
                  ].map((row) => (
                    <div key={row.state} className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${row.dot} shrink-0`} />
                      <span className="text-[10px] uppercase tracking-widest text-white/50 w-9 shrink-0">{row.label}</span>
                      <Select value={row.value} onValueChange={row.set}>
                        <SelectTrigger data-testid={row.testid} className="bg-[#0A0A0A] border-white/10 text-white h-9 font-bebas text-sm tracking-wide flex-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#111112] border-white/10 text-white">
                          {SOUND_TYPES.map((s) => (
                            <SelectItem key={s.id} value={s.id} className="font-bebas focus:bg-pink-500/20 focus:text-pink-200">{s.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-white/50 block mb-1.5">
                Volume <span className={`ml-1 font-mono tabular-nums ${volume > 1 ? 'text-amber-400' : 'text-white/40'}`}>{Math.round((muted ? 0 : volume) * 100)}%</span>
                {volume > 1 && !muted && <span className="ml-1.5 text-[8px] text-amber-400 uppercase tracking-widest">Boost</span>}
              </label>
              <div className="flex items-center gap-2 h-11">
                <button data-testid="seq-mute-btn" onClick={() => setMuted((m) => !m)} className="h-11 w-11 rounded-md border border-white/10 hover:border-white/30 flex items-center justify-center shrink-0">
                  {muted ? <VolumeX className="w-4 h-4 text-rose-400" /> : <Volume2 className={`w-4 h-4 ${volume > 1 ? 'text-amber-400' : ''}`} />}
                </button>
                <Slider
                  data-testid="seq-volume-slider"
                  value={[muted ? 0 : volume * 100]}
                  min={0}
                  max={400}
                  step={1}
                  onValueChange={(v) => { setVolume(v[0] / 100); if (v[0] > 0) setMuted(false); }}
                />
              </div>
            </div>
          </div>

          <p className="hidden sm:block text-[10px] uppercase tracking-[0.2em] text-white/30">
            Imposta tempo (numeratore/denominatore) singolarmente su ogni bar
          </p>
        </section>

        {/* Bars list */}
        <section className="flex flex-col gap-3" data-testid="seq-bars-list">
          {bars.map((bar, idx) => (
            <BarRow
              key={idx}
              idx={idx}
              bar={bar}
              isCurrent={currentBar === idx}
              currentCell={currentCell}
              isPlaying={isPlaying}
              onCycleCell={handleCycleCell}
              onSetNumerator={handleSetNumerator}
              onSetDenominator={handleSetDenominator}
              onToggleMute={handleToggleMute}
              onCopyBar={handleCopyBar}
              onResetBar={handleResetBar}
              onRequestDelete={handleRequestDelete}
              onDuplicateBar={handleDuplicateBar}
            />
          ))}
          <p data-testid="seq-gesture-hint" className="sm:hidden text-[10px] uppercase tracking-[0.2em] text-white/35 text-center px-2">
            Scorri ← per eliminare · tieni premuto per mute · doppio tocco per duplicare
          </p>
        </section>
        <DeleteBarDialog barIdx={pendingDelete} onConfirm={handleConfirmDelete} onCancel={() => setPendingDelete(null)} />

        {/* Bottom actions */}
        <section className="flex gap-2 flex-wrap">
          <button
            data-testid="seq-reset-all-btn"
            onClick={handleResetAll}
            className="h-10 px-4 rounded-md border border-white/10 hover:border-white/30 transition-all text-xs uppercase tracking-widest flex items-center gap-1.5"
          >
            <Trash2 className="w-3.5 h-3.5" /> Reset tutto
          </button>
          <button
            data-testid="seq-share-btn"
            onClick={handleShare}
            className="h-10 px-4 rounded-md border border-pink-400/40 hover:border-pink-400/70 hover:bg-pink-500/10 transition-all text-xs uppercase tracking-widest flex items-center gap-1.5 text-pink-300"
          >
            <Share2 className="w-3.5 h-3.5" /> Condividi
          </button>
        </section>

        <footer className="mt-6 mb-4">
          <AuthorSignature />
          <div className="text-center text-[10px] uppercase tracking-[0.4em] text-white/15 mt-4">
            Sequencer · time signature personalizzabile per bar
          </div>
        </footer>
      </div>

      <Toaster position="bottom-right" />
      <QRShareModal
        open={!!shareModal}
        url={shareModal?.url}
        title={shareModal?.title}
        subtitle={shareModal?.subtitle}
        onClose={() => setShareModal(null)}
      />
    </div>
  );
}
